/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-array-index-key */
import { useCallback, forwardRef, useEffect, useRef } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import gsap from 'gsap'
import injectSheet from 'react-jss'
import clsx from 'clsx'
import LangNav from '@/components/__new/LangNav'
import DelayLink from '@/components/DelayLink'
import actions from '@/actions'
import style from './style'

const Nav = forwardRef(({ classes }, ref) => {
  const $root = useRef()
  const $overlay = useRef()
  const $lang = useRef()
  const $nav = useRef()
  const q = gsap.utils.selector($root)

  const { compact, navOpen, mainMenu1, mainMenu2, mainMenu3, mainMenu4, mainMenu5, socialMenu, locales, translations } = useSelector((state) => ({
    mainMenu1: state.nav['mainMenu-1'] || [],
    mainMenu2: state.nav['mainMenu-2'] || [],
    mainMenu3: state.nav['mainMenu-3'] || [],
    mainMenu4: state.nav['mainMenu-4'] || [],
    mainMenu5: state.nav['mainMenu-5'] || [],
    socialMenu: state.nav.socialMenu || [],
    locales: state.locale.languages,
    translations: state.locale.translations,
    navOpen: state.nav.open,
    compact: state.nav.compact,
  }), shallowEqual)

  const dispatch = useDispatch()
  const setNavOpen = useCallback((index) => dispatch(actions.nav.setOpen(index)), [dispatch])

  /*------------------------------
  Close
  ------------------------------*/
  const handleClose = (e) => {
    if (e.type === 'keydown') {
      if (e.code === 'Escape') {
        setNavOpen(false)
      }
    } else {
      setNavOpen(false)
    }
  }

  useEffect(() => {
    if ($root.current) {
      gsap.set($root.current, { display: 'none' })
    }
  }, [])

  /*------------------------------
  Open Animation
  ------------------------------*/
  useEffect(() => {
    if ($root.current) {
      gsap.killTweensOf($root.current)
      gsap.set($root.current, {
        display: navOpen ? 'block' : 'none',
        delay: navOpen ? 0 : 1.2,
      })
    }
    if ($nav.current) {
      gsap.killTweensOf($nav.current)
      gsap.to($nav.current, {
        y: navOpen ? '0%' : '-100%',
        duration: 1.2,
        ease: 'power3.inOut',
      })
    }
    if (q('.navCol')) {
      gsap.killTweensOf(q('.navCol'))
      gsap.to(q('.navCol'), {
        y: navOpen ? '0' : '-50px',
        opacity: navOpen ? 1 : 0,
        stagger: 0.1,
        delay: 0.1,
        duration: 1.2,
        ease: 'power3.inOut',
      })
    }
    if ($overlay.current) {
      gsap.killTweensOf($overlay.current)
      gsap.to($overlay.current, {
        opacity: navOpen ? 1 : 0,
        duration: 1.2,
        ease: 'power3.inOut',
      })
    }
    if ($lang.current) {
      gsap.killTweensOf($lang.current)
      gsap.to($lang.current, {
        opacity: navOpen ? 1 : 0,
        duration: 0.5,
        delay: navOpen ? 1 : 0,
        ease: 'power3.out',
      })
    }

    if (navOpen) {
      window.addEventListener('keydown', handleClose)
    } else {
      window.removeEventListener('keydown', handleClose)
    }

    return () => {
      window.removeEventListener('keydown', handleClose)
    }
  }, [navOpen])

  /*------------------------------
  Render Nav
  ------------------------------*/
  const renderNav = (nav, index) => {
    return nav?.length > 0 ? (
      <div
        ref={ref}
        className={clsx({
          [classes.navCol]: true,
          [classes[`navCol${index}`]]: true,
          navCol: true,
        })}
      >
        {nav.map((item, ind) => {
          return (
            <div
              className={classes.navItem}
              key={`navCol${ind}`}
            >
              {item.url === '#' ? ( // eslint-disable-line no-nested-ternary
                <button className={classes.itemBig}>
                  {item.title}
                </button>
              ) : item.target === '_blank' ? (
                <a
                  className={`${classes.itemBig} ${item.classes}`}
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {item.title}
                  {item.classes.includes('prima') ? <svg xmlns="http://www.w3.org/2000/svg" width="43" height="15"><path fill="#fff" d="M4.427 0c-.965 0-1.684.334-2.145.995V.653a.411.411 0 0 0-.424-.426H.424A.412.412 0 0 0 0 .653v13.921c0 .246.178.426.424.426H1.86a.411.411 0 0 0 .424-.426v-2.66c.466.666 1.185 1.003 2.145 1.003 1.403 0 2.339-.704 2.782-2.093.24-.752.31-1.417.31-4.378 0-2.961-.073-3.64-.31-4.353C6.766.704 5.83 0 4.427 0Zm.81 6.446c0 2.668-.05 2.964-.167 3.363-.212.634-.636.942-1.297.942-.662 0-1.11-.327-1.32-.938-.118-.4-.17-.691-.17-3.366 0-2.675.052-2.94.167-3.334.213-.618.671-.946 1.323-.946.652 0 1.086.31 1.296.938.117.402.169.69.169 3.342v-.001ZM42.577.228h-1.435a.411.411 0 0 0-.424.426v.342C40.257.334 39.538 0 38.573 0c-1.403 0-2.339.704-2.782 2.091-.238.715-.311 1.409-.311 4.355 0 2.946.07 3.626.31 4.378.443 1.389 1.379 2.093 2.782 2.093.96 0 1.679-.337 2.145-1.003v.555c0 .247.178.426.424.426h1.435A.411.411 0 0 0 43 12.47V.653a.411.411 0 0 0-.424-.426v.001Zm-1.86 6.219c0 2.675-.051 2.965-.166 3.358-.213.619-.671.946-1.323.946-.652 0-1.086-.309-1.296-.938-.117-.402-.169-.691-.169-3.366 0-2.675.052-2.94.167-3.335.212-.636.636-.945 1.298-.945.663 0 1.11.328 1.32.938.118.4.17.69.17 3.342ZM14.576.055A2.72 2.72 0 0 0 14.062 0c-.966 0-1.754.41-2.27 1.165V.653a.411.411 0 0 0-.424-.426H9.933a.411.411 0 0 0-.424.426v11.611c0 .247.177.426.424.426h1.435a.411.411 0 0 0 .424-.426V4.777c0-1.546.68-2.433 1.867-2.433h.378c.243 0 .4-.119.468-.36L14.857.55c.05-.158.016-.266-.022-.33a.364.364 0 0 0-.26-.165ZM16.76.228h1.435c.234 0 .424.19.424.426v11.611c0 .235-.19.426-.424.426H16.76a.425.425 0 0 1-.424-.426V.653c0-.235.19-.426.424-.426ZM30.323 0c-1.119 0-1.982.437-2.516 1.268C27.35.426 26.529 0 25.362 0c-.905 0-1.649.362-2.145 1.03V.653a.411.411 0 0 0-.424-.426h-1.435a.411.411 0 0 0-.424.426v11.611c0 .247.178.426.424.426h1.435a.411.411 0 0 0 .424-.426V4.195c0-1.346.485-2.028 1.44-2.028 1.268 0 1.414 1.158 1.414 2.028v8.07c0 .246.178.425.424.425h1.435a.411.411 0 0 0 .425-.426v-8.12c0-.902.245-1.978 1.414-1.978 1.29 0 1.44 1.158 1.44 2.028v8.07c0 .246.177.425.423.425h1.436a.411.411 0 0 0 .424-.426V3.892C33.492 1.309 32.425 0 30.323 0Z" /></svg> : null}
                </a>
              ) : (
                <DelayLink
                  className={`${classes.itemBig} ${item.classes}`}
                  to={item.url}
                  onMouseUp={() => setNavOpen(false)}
                >
                  {item.title}
                  {item.classes.includes('prima') ? <svg xmlns="http://www.w3.org/2000/svg" width="43" height="15"><path fill="#fff" d="M4.427 0c-.965 0-1.684.334-2.145.995V.653a.411.411 0 0 0-.424-.426H.424A.412.412 0 0 0 0 .653v13.921c0 .246.178.426.424.426H1.86a.411.411 0 0 0 .424-.426v-2.66c.466.666 1.185 1.003 2.145 1.003 1.403 0 2.339-.704 2.782-2.093.24-.752.31-1.417.31-4.378 0-2.961-.073-3.64-.31-4.353C6.766.704 5.83 0 4.427 0Zm.81 6.446c0 2.668-.05 2.964-.167 3.363-.212.634-.636.942-1.297.942-.662 0-1.11-.327-1.32-.938-.118-.4-.17-.691-.17-3.366 0-2.675.052-2.94.167-3.334.213-.618.671-.946 1.323-.946.652 0 1.086.31 1.296.938.117.402.169.69.169 3.342v-.001ZM42.577.228h-1.435a.411.411 0 0 0-.424.426v.342C40.257.334 39.538 0 38.573 0c-1.403 0-2.339.704-2.782 2.091-.238.715-.311 1.409-.311 4.355 0 2.946.07 3.626.31 4.378.443 1.389 1.379 2.093 2.782 2.093.96 0 1.679-.337 2.145-1.003v.555c0 .247.178.426.424.426h1.435A.411.411 0 0 0 43 12.47V.653a.411.411 0 0 0-.424-.426v.001Zm-1.86 6.219c0 2.675-.051 2.965-.166 3.358-.213.619-.671.946-1.323.946-.652 0-1.086-.309-1.296-.938-.117-.402-.169-.691-.169-3.366 0-2.675.052-2.94.167-3.335.212-.636.636-.945 1.298-.945.663 0 1.11.328 1.32.938.118.4.17.69.17 3.342ZM14.576.055A2.72 2.72 0 0 0 14.062 0c-.966 0-1.754.41-2.27 1.165V.653a.411.411 0 0 0-.424-.426H9.933a.411.411 0 0 0-.424.426v11.611c0 .247.177.426.424.426h1.435a.411.411 0 0 0 .424-.426V4.777c0-1.546.68-2.433 1.867-2.433h.378c.243 0 .4-.119.468-.36L14.857.55c.05-.158.016-.266-.022-.33a.364.364 0 0 0-.26-.165ZM16.76.228h1.435c.234 0 .424.19.424.426v11.611c0 .235-.19.426-.424.426H16.76a.425.425 0 0 1-.424-.426V.653c0-.235.19-.426.424-.426ZM30.323 0c-1.119 0-1.982.437-2.516 1.268C27.35.426 26.529 0 25.362 0c-.905 0-1.649.362-2.145 1.03V.653a.411.411 0 0 0-.424-.426h-1.435a.411.411 0 0 0-.424.426v11.611c0 .247.178.426.424.426h1.435a.411.411 0 0 0 .424-.426V4.195c0-1.346.485-2.028 1.44-2.028 1.268 0 1.414 1.158 1.414 2.028v8.07c0 .246.178.425.424.425h1.435a.411.411 0 0 0 .425-.426v-8.12c0-.902.245-1.978 1.414-1.978 1.29 0 1.44 1.158 1.44 2.028v8.07c0 .246.177.425.423.425h1.436a.411.411 0 0 0 .424-.426V3.892C33.492 1.309 32.425 0 30.323 0Z" /></svg> : null}
                </DelayLink>
              )}
              {item.children && item.children?.length > 0 ? (
                <ul>
                  {item.children && item.children.map((child, i) => (
                    <li
                      key={`children${i}`}
                      className={classes.itemSmall}
                    >
                      {child.target === '_blank' ? (
                        <a
                          href={child.url}
                          target="_blank"
                          rel="noreferrer"
                          className={child.classes}
                        >
                          {child.title}
                        </a>
                      ) : (
                        <DelayLink
                          to={child.url}
                          onMouseUp={() => setNavOpen(false)}
                          className={child.classes}
                        >
                          {child.title}
                        </DelayLink>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          )
        })}
      </div>
    ) : null
  }

  return (
    <div
      ref={$root}
      className={clsx({
        [classes.root]: true,
        [classes.compact]: compact,
        newNav: true,
      })}
      data-lenis-prevent
    >
      <div
        ref={$overlay}
        className={classes.overlay}
        onClick={handleClose}
      />
      <div
        ref={$lang}
        className={classes.langNavDesktop}
      >
        <LangNav
          languages={locales}
          translations={translations}
        />
      </div>
      <div
        ref={$nav}
        className={classes.nav}
        data-lenis-prevent
      >
        {renderNav(mainMenu1, 1)}
        {renderNav(mainMenu2, 2)}
        {renderNav(mainMenu3, 3)}
        {renderNav(mainMenu4, 4)}

        <ul className={classes.langNavMobile}>
          {locales.map((obj, index) => {
            return (
              <li
                key={`key-${index}`}
                className={classes.item}
              >
                <a href={`/${obj}`} className={classes.parentItem}>{obj}</a>
              </li>
            )
          })}
        </ul>

        {renderNav(mainMenu5, 5)}
        {renderNav(socialMenu, 6)}
      </div>
    </div>
  )
})

export default injectSheet(style)(Nav)
